/**
 * Shared project create/match selection UI. Extracted from PostParseProjectDialog
 * so the post-parse dialog and the new PreImportDialog render the same fuzzy/exact
 * match picker (DRY). Renders ONLY the selection surface — the caller owns the
 * surrounding chrome (modal, header summary, create-form swap).
 */
import type { Project, ProjectMatchResponse } from '../../types';
import { getProjectDisplayName } from '../../lib/utils';
import { Button } from './ui/button';
import { Pill } from './ui/pill';
import { IconCheck, IconPlus } from './icons';

export interface ProjectMatchPickerProps {
  /** Match result { exact_match, fuzzy_matches }. */
  match: ProjectMatchResponse;
  /** User picked an existing project. */
  onSelect: (_project: Project) => void;
  /** User chose to create a new project. */
  onCreateNew: () => void;
  /**
   * Id of the project currently selected by the caller — reflected as a done
   * state on its row/button so the click isn't a silent no-op. (B-489)
   */
  selectedProjectId?: string | null;
  /**
   * The caller has staged "create a new project" — disables + relabels the
   * create button so it can't be fired twice (no duplicate projects). (B-489)
   */
  createNewSelected?: boolean;
}

/** Exact/fuzzy/no-match project picker shared by both import dialogs. */
export default function ProjectMatchPicker({
  match,
  onSelect,
  onCreateNew,
  selectedProjectId = null,
  createNewSelected = false,
}: ProjectMatchPickerProps) {
  const exactMatch = match?.exact_match ?? null;
  const fuzzyMatches = match?.fuzzy_matches ?? [];

  // The create button reflects whether "new project" is already staged. When it
  // is, it disables + swaps to a checked "New project selected" so the user gets
  // feedback and can't double-fire it (the live bug: button looked unclicked).
  const createBtn = (variant: 'primary' | 'ghost') => (
    <Button
      variant={createNewSelected ? 'secondary' : variant}
      size="sm"
      onClick={onCreateNew}
      disabled={createNewSelected}
      data-testid="create-new-project"
    >
      {createNewSelected ? (
        <>
          <IconCheck /> New project selected
        </>
      ) : (
        <>
          <IconPlus /> Create new project
        </>
      )}
    </Button>
  );

  if (exactMatch) {
    return (
      <div className="space-y-2">
        <p className="text-[12.5px] text-[var(--tx-mid)]">
          This estimate&apos;s claim number matches an existing project. Use it, or create a new
          project from the estimate header.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => onSelect(exactMatch)}
            disabled={selectedProjectId === exactMatch.id}
          >
            {selectedProjectId === exactMatch.id ? <IconCheck /> : null}
            Use {getProjectDisplayName(exactMatch)}
          </Button>
          {createBtn('ghost')}
        </div>
      </div>
    );
  }

  if (fuzzyMatches.length > 0) {
    return (
      <div className="space-y-2">
        <p className="text-[12.5px] text-[var(--tx-mid)]">
          We found similar projects. Pick one to import into, or create a new project.
        </p>
        <ul className="divide-y divide-[var(--bd-subtle)] overflow-hidden rounded-[var(--r-md)] border border-[var(--bd-subtle)]">
          {fuzzyMatches.map((p) => {
            const score = p._match_score ?? 0;
            const confidence = score >= 0.9 ? 'High' : score >= 0.8 ? 'Medium' : 'Low';
            const isSelected = selectedProjectId === p.id;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onSelect(p)}
                  aria-pressed={isSelected}
                  className={`flex w-full items-start justify-between gap-2 px-3 py-2 text-left hover:bg-[var(--bg-3)] ${
                    isSelected ? 'bg-[var(--bg-3)]' : ''
                  }`}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[12.5px] font-[550] text-[var(--tx-hi)]">
                      {getProjectDisplayName(p)}
                    </span>
                    <span className="truncate text-[11px] text-[var(--tx-lo)]">
                      {[p.address, p.claim_number].filter(Boolean).join(' · ') || 'No address'}
                    </span>
                  </span>
                  {isSelected ? (
                    <Pill kind="ok">
                      <IconCheck /> Selected
                    </Pill>
                  ) : (
                    <Pill kind="neutral">{confidence} match</Pill>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {createBtn('primary')}
      </div>
    );
  }

  // no match
  return (
    <div className="space-y-2">
      <p className="text-[12.5px] text-[var(--tx-mid)]">
        No matching project found. Create a new project prefilled from the estimate header.
      </p>
      {createBtn('primary')}
    </div>
  );
}
